import type { ReactNode } from "react"
import { Link } from "@inertiajs/react"
import { ClipboardList, TreePine } from "lucide-react"

export type SharedFormsProps = {
  organizationName: string | null
  formsPath: string
  instructionsPath: string
}

/**
 * Chrome for the operational forms when they're reached through the Christmas
 * tree rather than an account (SharedFormsAccess).
 *
 * Deliberately not the app shell: that sidebar is built from a route table
 * keyed on a signed-in user, so a shared visitor gets it standing empty, with a
 * "Sign out" of a session they never started. The two places they can actually
 * go are the forms list and the instruction sheet they came from, so those are
 * the only two things here.
 */
export function SharedFormsLayout({
  shared,
  children,
}: {
  shared: SharedFormsProps
  children: ReactNode
}) {
  return (
    <div className="flex min-h-svh flex-col bg-background">
      <header className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-4 py-3">
        <Link
          href={shared.formsPath}
          className="min-w-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="font-semibold tracking-tight">FishingCreek</span>
          {shared.organizationName && (
            <span className="block truncate text-sm text-muted-foreground">
              {shared.organizationName}
            </span>
          )}
        </Link>

        <nav aria-label="Shared forms" className="ms-auto flex items-center gap-4 text-sm">
          <Link
            href={shared.formsPath}
            className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"
          >
            <ClipboardList className="h-4 w-4" aria-hidden="true" />
            Forms
          </Link>
          {/* Leaves the SPA on purpose: the instruction sheet is served outside
              the app shell, and an Inertia visit would ask it for a page object. */}
          <a
            href={shared.instructionsPath}
            className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"
          >
            <TreePine className="h-4 w-4" aria-hidden="true" />
            Instructions
          </a>
        </nav>
      </header>

      {/* min-w-0 so a wide table or form row can't push the page sideways on a
          phone, which is where most of these get filled. */}
      <main className="min-w-0 flex-1">{children}</main>
    </div>
  )
}
